import { useState, useMemo } from "react";
import { useGetAllPlanting } from "../../api/scouting";
import { AppButton } from "../../components/ui/AppButton";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../components/ui/data-table";
import { Icons } from "../../assets/images/icons";
import { useTablePrint } from "../../hooks/useTablePrint";
import { Planting } from "../../types/planting";
import { formatDateToISO } from "../../util/date-formatter";
import { ExportButton } from "../../components/ui/ExportButton";
import { Input } from "../../components/ui/input";

const PlantingPage = () => {
  const navigate = useNavigate();

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: plantingData, isLoading } = useGetAllPlanting({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  /** Transform API data */
  const transformedPlantingData = useMemo((): Planting[] => {
    if (!plantingData?.data?.content) return [];

    return plantingData.data.content.map((item: any) => ({
      id: item.id,
      date: item.date,
      plantingDensity: item.plantingDensity || 0,
      emergenceRate: item.emergenceRate,
      createdAt: item.createdAt,
      createdBy: item.createdBy,
    }));
  }, [plantingData]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleRowClick = (record: any) => {
    navigate(`/scouting/planting/${record.id}`);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  /** Define columns for the DataTable */
  const plantingColumns = [
    {
      key: "date",
      title: "Date",
      dataIndex: "date",
      align: "left" as const,
    },
    {
      key: "plantingDensity",
      title: "Planting Density",
      dataIndex: "plantingDensity",
      align: "left" as const,
    },
    {
      key: "emergenceRate",
      title: "Emergence Rate (%)",
      dataIndex: "emergenceRate",
      align: "right" as const,
    },
    {
      key: "createdAt",
      title: "Created At",
      dataIndex: "createdAt",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "createdBy",
      title: "Created By",
      dataIndex: "createdBy",
      align: "left" as const,
    },
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between">
        {/* Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search here..."
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(
                plantingColumns,
                transformedPlantingData,
                "Pre-Planting Report"
              )
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>
          <ExportButton
            columns={plantingColumns}
            data={transformedPlantingData}
            fileName="planting"
            title="Planting Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: plantingData?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <DataTable<Planting>
          data={transformedPlantingData}
          columns={plantingColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: plantingData?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No item found"
          rowKey="id"
        />
      </div>
    </>
  );
};

export default PlantingPage;
